import React, { useState, useRef } from "react";
import axios from "axios";
import churnIcon from "../assets/img/bank-churn.jpg";
import homeIcon from "../assets/icons/thinkIcons/Home.png";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
// import "animate.css";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";

const ChurnPrediction = () => {
  const navigate = useNavigate();
  const resultRef = useRef(null); // 👈 for scroll
  const [formData, setFormData] = useState({
    credit_score: "",
    geography: "",
    gender: "",
    age: "",
    tenure: "",
    balance: "",
    num_of_products: "",
    has_credit_card: "",
    is_active_member: "",
    estimated_salary: "",
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePredict = async () => {
    try {
      setLoading(true);
      setResult(null);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/churn/predict`,
        formData
      );
      setResult(res.data);
      showSuccessToast("Predicted Values Successfully !!!");

      // 👇 scroll to result after prediction
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 300);
      
    } catch (err) {
      console.error("Prediction Error:", err);
      showErrorToast("Prediction Failed !!!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Home Button */}
      <div className="flex justify-end">
        <button
          className="flex items-center gap-2 border px-3 py-2 mb-2 rounded-full"
          onClick={() => navigate("/user/dashboard")}
        >
          <img
            src={homeIcon}
            alt="Home Icon"
            style={{ width: "18px", height: "18px" }}
          />
          <span className="text-xs">Home</span>
        </button>
      </div>

      {/* Header Card */}
      <div className="card mb-4 animate__animated animate__fadeInDown">
        <div className="card-body d-flex align-items-center">
          <img
            src={churnIcon}
            alt="Churn Prediction"
            className="rounded me-3"
            style={{ width: "80px", height: "80px", objectFit: "cover" }}
          />
          <div>
            <h6 className="mb-1">Customer Churn Prediction</h6>
            <p className="mb-0 text-muted">
              Predict the probability of a bank customer leaving.
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="pothole-body">
        <p className="fw-semibold">| Enter Customer Details for Prediction</p>

        <div className="card shadow-sm animate__animated animate__fadeInUp">
          <div className="card-body">
            <div className="row g-3">
              {/* Form Fields */}
              {[  { label: "Credit Score", name: "credit_score", type: "number" },
                {
                  label: "Geography",
                  name: "geography",
                  type: "select",
                  options: ["France", "Germany", "Spain"],
                },
                {
                  label: "Gender",
                  name: "gender",
                  type: "select",
                  options: ["Male", "Female"],
                },
                { label: "Age", name: "age", type: "number" },
                { label: "Tenure", name: "tenure", type: "number" },
                {
                  label: "Balance",
                  name: "balance",
                  type: "number",
                  step: "0.01",
                },
                {
                  label: "Number of Products",
                  name: "num_of_products",
                  type: "number",
                },
                {
                  label: "Has Credit Card",
                  name: "has_credit_card",
                  type: "select",
                  options: [
                    { label: "Yes", value: "1" },
                    { label: "No", value: "0" },
                  ],
                },
                {
                  label: "Is Active Member",
                  name: "is_active_member",
                  type: "select",
                  options: [
                    { label: "Yes", value: "1" },
                    { label: "No", value: "0" },
                  ],
                },
                {
                  label: "Estimated Salary",
                  name: "estimated_salary",
                  type: "number",
                  step: "0.01",
                } ].map((field, idx) => (
                <div className="col-md-6" key={idx}>
                  <label className="form-label fw-semibold">
                    {field.label}
                  </label>
                  {field.type === "select" ? (
                    <select
                      className="form-select form-control"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      {field.options.map((opt) =>
                        typeof opt === "string" ? (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ) : (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        )
                      )}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      step={field.step || undefined}
                      className="form-control"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      required
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-4">
              <button
                className="btn pothole-btn border px-5"
                disabled={loading}
                onClick={handlePredict}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Predicting...
                  </>
                ) : (
                  "Predict & Deliver"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Result Card */}
      {result && (
        <div
          ref={resultRef}
          className="card shadow-sm mt-4 animate__animated animate__fadeInUp"
        >
          <div className="card-body text-start">
            <u className="text-primary">
              <h5 className="text-primary fw-semibold">Prediction Result</h5>
            </u>
            <p className="mb-2">Customer Churn Analysis</p>
            <h6>
              Churn Probability:{" "}
              <span className="fw-bold">
                {(result.churn_probability * 100).toFixed(2)}%
              </span>
            </h6>
            <h6>
              Status:{" "}
              {result.will_churn ? (
                <span className="text-danger fw-bold">Will Churn</span>
              ) : (
                <span className="text-success fw-bold">Will Stay</span>
              )}
            </h6>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurnPrediction;
